import { NeoStroke } from "../structures/NeoStroke";
import { isSamePage, makeNPageIdStr } from "../util";
export class ReplayStorage {
    constructor() {
        this.timeBased = [];
        /**
         * 페이지가 바뀔 때마다를 하나의 청크로 삼은 것
         * @memberof ReplayStorage
         */
        this.pageChunks = [];
        this.continousStrokesChunk = [];
        this.getChunkStartTime_abs = (chkIdx) => {
            const chunk = this.pageChunks[chkIdx];
            return chunk.startTime_abs;
        };
        this.getChunkEndTime_abs = (chkIdx) => {
            const chunk = this.pageChunks[chkIdx];
            return chunk.endTime_abs;
        };
        this.getChunkStartDeltaTime = (chkIdx) => {
            const chunk = this.pageChunks[chkIdx];
            return chunk.startDeltaTime;
        };
        this.getChunkEndDeltaTime = (chkIdx) => {
            const chunk = this.pageChunks[chkIdx];
            return chunk.endDeltaTime;
        };
        this.getChunkPageInfo = (chkIdx) => {
            if (this.pageChunks[chkIdx])
                return this.pageChunks[chkIdx].pageInfo;
            return undefined;
        };
        this.getChunkIndex = (deltaTime) => {
            for (let i = this.pageChunks.length - 1; i >= 0; i--) {
                if (deltaTime >= this.pageChunks[i].startDeltaTime)
                    return i;
            }
            return 0;
        };
        /**
         * ths keys are based on makeNPageIdStr({ section, owner, book, page } )
         * @type {{ [key: string]: NeoStroke[] }}
         * @memberof ReplayStorage
         */
        this.pageBased = {};
        this.getPageStrokes = (pageInfo) => {
            const pgStrokes = this.pageBased[makeNPageIdStr(pageInfo)];
            return pgStrokes ? pgStrokes : [];
        };
        /**
         * build replay strokes with REST response
         *
         * @param {InkstoreReponseDataType} response
         * @param {number} [ignorableItemTime] - 연속되는 필기라고 생각할 수 있는 스트로크간 비는 시간
         * @memberof ReplayStorage
         */
        this.fromInkstore = (resultElements, ignorableItemTime) => {
            // 초기화
            this.init();
            // 스트로크 채워 넣기
            resultElements.forEach(element => {
                this.interpreteInkstoreData(element.data);
            });
            // 끝내기
            this.finish(ignorableItemTime);
        };
        /**
         * build replay strokes with NeoStroke array
         *
         * @param {NeoStroke[]} strokes
         * @param {number} [ignorableItemTime=5*60*1000] - 연속되는 필기라고 생각할 수 있는 스트로크간 비는 시간
         */
        this.fromNeoStrokes = (strokes, ignorableItemTime) => {
            // 초기화
            this.init();
            // 스트로크 채워 넣기
            strokes.forEach(stroke => this.addStroke(stroke));
            // 끝내기
            this.finish(ignorableItemTime);
        };
        this.init = () => {
            this.timeBased = [];
            this.pageBased = {};
        };
        this.finish = (ignorableItemTime) => {
            this.sort();
            // this.continousStrokesChunk = this.makeCoutinousStrokeChunk(this.timeBased, ignorableItemTime);
            // this.pageChunks = this.makePageChunks(this.timeBased);
            this.pageChunks = this.makeCoutinousStrokeChunk(this.timeBased, ignorableItemTime);
        };
        /**
         * 스트로크 배열을, 페이지별로, 그리고 "정해진 시간" 내에 연속되는 것들을 그룹핑
         * 각 청크들의 시간 길이의 합이 해당 페이지의 공부 시간이다.
         * 기본값은 IGNORABLE_IDLE_TIME (ms 단위)에 정해져 있다.
         *
         * 이런 식으로 생성될 수 있다. pgae1 A chunk => page1 B chunk => page2 C chunk => page1 D chunk => ...
         *
         * @private
         * @param {NeoStroke[]} strokes
         * @param {number} ignorableItemTime
         * @memberof ReplayStorage
         */
        this.makeCoutinousStrokeChunk = (strokes, ignorableItemTime) => {
            /** IDLE 시간 이내, 같은 페이지의 청크 */
            const chunks = [];
            const firstStroke_startTime_abs = strokes[0].startTime;
            let prevSOBP = null;
            let lastTime = Number.MAX_VALUE;
            // 주어진 배열을 탐색
            for (const stroke of strokes) {
                const currSOBP = {
                    section: stroke.section,
                    owner: stroke.owner,
                    book: stroke.book,
                    page: stroke.page,
                };
                const lastChunk = chunks[chunks.length - 1];
                const startTime = stroke.startTime;
                /** 페이지가 바뀔 때마다, 그리고, 시간 간격이 "정해진 시간" 이상으로 떨어졌을 때, 생성되는 청크에 대한 처리 */
                if (!lastChunk || !isSamePage(prevSOBP, currSOBP) || (startTime - lastTime) > ignorableItemTime) {
                    chunks.push({
                        pageInfo: currSOBP,
                        startTime_abs: stroke.startTime,
                        endTime_abs: stroke.endTime,
                        startDeltaTime: stroke.startTime - firstStroke_startTime_abs,
                        endDeltaTime: stroke.endTime - firstStroke_startTime_abs,
                        strokes: [stroke],
                    });
                    prevSOBP = Object.assign({}, currSOBP);
                }
                else {
                    lastChunk.endTime_abs = stroke.endTime;
                    lastChunk.endDeltaTime = stroke.endTime - firstStroke_startTime_abs;
                    lastChunk.strokes.push(stroke);
                }
                lastTime = stroke.endTime;
            }
            return chunks;
        };
        /**
         * 스트로크 배열을, 같은 페이지별로 그룹핑해서 나눈 청크를 생성
         * 이런 식으로 생성될 수 있다. pgae1 => page2 => page1 => page3 => ...
         *
         * @private
         * @param {NeoStroke[]} strokes
         * @memberof ReplayStorage
         */
        this.makePageChunks = (strokes) => {
            /** 페이지가 바뀔 때마다 생성되는 청크 */
            const chunks = [];
            const firstStroke_startTime_abs = strokes[0].startTime;
            let prev = null;
            // 주어진 배열을 탐색
            for (const stroke of strokes) {
                const curr = {
                    section: stroke.section,
                    owner: stroke.owner,
                    book: stroke.book,
                    page: stroke.page,
                };
                const lastChunk = chunks[chunks.length - 1];
                /** 페이지가 바뀔 때마다 생성되는 청크에 대한 처리 */
                if (!lastChunk || !isSamePage(prev, curr)) {
                    chunks.push({
                        pageInfo: curr,
                        startTime_abs: stroke.startTime,
                        endTime_abs: stroke.endTime,
                        startDeltaTime: stroke.startTime - firstStroke_startTime_abs,
                        endDeltaTime: stroke.endTime - firstStroke_startTime_abs,
                        strokes: [stroke],
                    });
                    prev = Object.assign({}, curr);
                }
                else {
                    lastChunk.endTime_abs = stroke.endTime;
                    lastChunk.endDeltaTime = stroke.endTime - firstStroke_startTime_abs;
                    lastChunk.strokes.push(stroke);
                }
            }
            return chunks;
        };
        this.interpreteInkstoreData = (data) => {
            const binary_string = window.atob(data);
            const json = JSON.parse(binary_string);
            const storedPage = json.page;
            const { section, owner, bookCode: book, pageNumber: page } = storedPage;
            const pageInfo = { section, owner, book, page };
            // 스트로크 채워 넣기
            storedPage.strokes.forEach(stroke => {
                const neoStroke = NeoStroke.fromInkstoreStroke({ stroke, pageInfo });
                this.addStroke(neoStroke);
            });
        };
        this.addStroke = (stroke) => {
            const { section, owner, book, page } = stroke;
            const pageKey = makeNPageIdStr({ section, owner, book, page });
            // 페이지 순서에 채워 넣기
            let pageStrokes = this.pageBased[pageKey];
            if (pageStrokes === undefined) {
                this.pageBased[pageKey] = [];
                pageStrokes = this.pageBased[pageKey];
            }
            pageStrokes.push(stroke);
            // 시간 순서에 채워 넣기
            this.timeBased.push(stroke);
        };
        this.sort = () => {
            for (const pageId in this.pageBased) {
                const arr = this.pageBased[pageId];
                arr.sort((a, b) => a.startTime - b.startTime);
            }
            this.timeBased.sort((a, b) => a.startTime - b.startTime);
        };
    }
    get numStrokes() {
        return this.timeBased.length;
    }
    get startTime() {
        if (this.timeBased.length === 0)
            return -1;
        return this.timeBased[0].startTime;
    }
    get endTime() {
        if (this.timeBased.length === 0)
            return -1;
        return this.timeBased[this.timeBased.length - 1].endTime;
    }
    get strokes() {
        return this.timeBased;
    }
    get duration() {
        return this.endTime - this.startTime;
    }
}
//# sourceMappingURL=ReplayStorage.js.map